import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";

/* ═══════════════════════════════════════════════════════════════
   RightClickMenuContext

   Provides a global right-click context menu system.

   Concepts:
   - **Zone**: A named region of the UI (e.g. "feed-custom-view").
     Components register menu items for their zone via useContextMenu().
   - **Resolution**: On right-click, the handler walks up the DOM from
     event.target looking for [data-context-menu]. If found, it reads
     the zone name and payload, then renders zone-specific items.
   - **Global items**: Always-available items (Ask Kernel, Copy, Save)
     that appear below zone-specific items when text is selected.

   Usage:
     <RightClickMenuProvider>
       <App />
     </RightClickMenuProvider>

     // In any component that owns contextual items:
     useContextMenu("feed-custom-view", [
       { label: "Edit View",  icon: Pencil, action: (payload) => { ... } },
       { label: "Delete View", icon: Trash2,  action: (payload) => { ... }, variant: "danger" },
     ]);

     // In the JSX for that component, tag elements:
     <div
       data-context-menu="feed-custom-view"
       data-context-payload={JSON.stringify(viewData)}
     >

   Menu item shape:
     {
       label: string,
       icon: LucideIcon,
       action: (payload: any) => void,
       variant?: "danger",           // red text on hover
       disabled?: boolean,
       hidden?: (payload) => boolean, // conditionally hide
     }
   ═══════════════════════════════════════════════════════════════ */

const RightClickMenuContext = createContext(null);

export const useRightClickMenu = () => {
  const ctx = useContext(RightClickMenuContext);
  if (!ctx)
    throw new Error(
      "useRightClickMenu must be used within RightClickMenuProvider",
    );
  return ctx;
};

/* ── Registration hook ── */
export const useContextMenu = (zone, items) => {
  const ctx = useContext(RightClickMenuContext);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    if (!ctx) return;
    ctx.registerZone(zone, itemsRef);
    return () => ctx.unregisterZone(zone, itemsRef);
  }, [zone, ctx]);
};

/* ── #6: CSS highlight for the active right-click target ── */
const TARGET_CLASS = "context-menu-active-target";

const injectTargetStyle = (() => {
  let done = false;
  return () => {
    if (done) return;
    done = true;
    const s = document.createElement("style");
    s.textContent = `.${TARGET_CLASS}{outline:1.5px solid rgba(14,165,233,0.25)!important;outline-offset:-1px;border-radius:6px}`;
    document.head.appendChild(s);
  };
})();

/* ── Provider ── */
export const RightClickMenuProvider = ({ children }) => {
  const [menuState, setMenuState] = useState({
    visible: false,
    position: { x: 0, y: 0 },
    zone: null,
    payload: null,
    selectedText: "",
  });

  const targetElRef = useRef(null); // #6: highlighted element
  const closingRef = useRef(false); // #9: prevents double-close race

  // Zone registry: Map<zoneName, Set<ref>>
  const zonesRef = useRef(new Map());

  const registerZone = useCallback((zone, itemsRef) => {
    if (!zonesRef.current.has(zone)) zonesRef.current.set(zone, new Set());
    zonesRef.current.get(zone).add(itemsRef);
  }, []);

  const unregisterZone = useCallback((zone, itemsRef) => {
    const set = zonesRef.current.get(zone);
    if (set) {
      set.delete(itemsRef);
      if (set.size === 0) zonesRef.current.delete(zone);
    }
  }, []);

  const getZoneItems = useCallback((zone) => {
    const set = zonesRef.current.get(zone);
    if (!set || set.size === 0) return [];
    const refs = [...set];
    return refs[refs.length - 1].current || [];
  }, []);

  /* ── #6: Target highlight helpers ── */
  const clearHighlight = useCallback(() => {
    if (targetElRef.current) {
      targetElRef.current.classList.remove(TARGET_CLASS);
      targetElRef.current = null;
    }
  }, []);

  const closeMenu = useCallback(() => {
    closingRef.current = true;
    clearHighlight();
    setMenuState((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    requestAnimationFrame(() => {
      closingRef.current = false;
    });
  }, [clearHighlight]);

  const openMenu = useCallback(
    (position, zone, payload, selectedText, targetEl) => {
      clearHighlight();
      if (targetEl) {
        targetEl.classList.add(TARGET_CLASS);
        targetElRef.current = targetEl;
      }
      setMenuState({ visible: true, position, zone, payload, selectedText });
    },
    [clearHighlight],
  );

  useEffect(() => {
    injectTargetStyle();
  }, []);

  /* ── Global event listeners ── */
  useEffect(() => {
    if (process.env.REACT_APP_DISABLE_CONTEXTMENU) return;

    const handleContextMenu = (event) => {
      const selectedText = window.getSelection()?.toString() || "";

      // Walk up DOM to find a zone
      let zone = null;
      let payload = null;
      let targetEl = null;
      let el = event.target;

      while (el && el !== document.body) {
        if (el.dataset?.contextMenu) {
          zone = el.dataset.contextMenu;
          targetEl = el;
          try {
            payload = el.dataset.contextPayload
              ? JSON.parse(el.dataset.contextPayload)
              : null;
          } catch {
            payload = null;
          }
          break;
        }
        el = el.parentElement;
      }

      // If no zone and no text selected, let native browser menu through
      if (!zone && selectedText.trim().length === 0) return;

      event.preventDefault();

      // #4: clientX/Y is viewport-relative, matching position:fixed
      openMenu(
        { x: event.clientX, y: event.clientY },
        zone,
        payload,
        selectedText,
        targetEl,
      );
    };

    // #9: Skip if we're already closing from a menu-item click
    const handleClick = () => {
      if (!closingRef.current) closeMenu();
    };

    // #7: Capture-phase scroll catches nested scroll containers
    const handleScroll = () => closeMenu();

    const handleKeyDown = (e) => {
      // Escape is also handled inside the menu for keyboard nav,
      // but this catches it globally as a fallback
      if (e.key === "Escape") closeMenu();
    };

    // #1: Mark right-click mousedown so navbar dropdowns can ignore it
    const handleMouseDown = (e) => {
      if (e.button === 2) {
        e.__contextMenuHandled = true;
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("click", handleClick);
    document.addEventListener("scroll", handleScroll, true);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleMouseDown, true);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("click", handleClick);
      document.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleMouseDown, true);
    };
  }, [openMenu, closeMenu]);

  const value = {
    menuState,
    closeMenu,
    registerZone,
    unregisterZone,
    getZoneItems,
  };

  return (
    <RightClickMenuContext.Provider value={value}>
      {children}
    </RightClickMenuContext.Provider>
  );
};

export default RightClickMenuContext;
